import React, { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";

const CarouselWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const ScrollContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${(p) => p.$gap || 16}px;
  overflow-x: auto;
  scroll-behavior: smooth;
  padding-bottom: 8px;

  /* Hide scrollbar */
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const ArrowButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 50%;
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: background-color 0.2s ease;

  &:hover {
    background: #f8f8f8;
  }
`;

const DotsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
`;

const Dot = styled.button`
  width: 8px;
  height: 8px;
  border: none;
  padding: 0;
  border-radius: 50%;
  background-color: ${(p) => (p.$active ? "#333333" : "#d0d0d0")};
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.2s ease;

  &:hover {
    background-color: ${(p) => (p.$active ? "#333333" : "#999999")};
  }
`;

const StreamCarousel = ({ items = [], scrollStep = 238, gap = 16, imagesCount = 1 }) => {
  const containerRef = useRef(null);
  const itemRefs = useRef([]);

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  // Checks screen overflow and calculates active dot index
  const checkOverflowAndActive = () => {
    if (!containerRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;

    setCanScrollLeft(scrollLeft > 5);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 5);

    // If scrolled to the end, highlight the last dot
    const isAtEnd = Math.ceil(scrollLeft + clientWidth) >= scrollWidth - 10;
    if (isAtEnd && items.length > 0) {
      setActiveIndex(items.length - 1);
      return;
    }

    let currentActive = 0;
    itemRefs.current.forEach((ref, idx) => {
      if (ref) {
        const offsetLeft = ref.offsetLeft - containerRef.current.offsetLeft;
        if (scrollLeft >= offsetLeft - ref.clientWidth / 2) {
          currentActive = idx;
        }
      }
    });
    setActiveIndex(currentActive);
  };

  useEffect(() => {
    checkOverflowAndActive();

    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", checkOverflowAndActive);
    }
    window.addEventListener("resize", checkOverflowAndActive);

    return () => {
      if (container) {
        container.removeEventListener("scroll", checkOverflowAndActive);
      }
      window.removeEventListener("resize", checkOverflowAndActive);
    };
  }, [items]);

  const handleScrollNext = () => {
    if (containerRef.current) {
      containerRef.current.scrollBy({ left: scrollStep, behavior: "smooth" });
    }
  };

  const handleScrollPrev = () => {
    if (containerRef.current) {
      containerRef.current.scrollBy({ left: -scrollStep, behavior: "smooth" });
    }
  };

  const handleDotClick = (index) => {
    const targetRef = itemRefs.current[index];
    if (targetRef && containerRef.current) {
      setActiveIndex(index);
      const targetLeft = targetRef.offsetLeft - containerRef.current.offsetLeft;
      containerRef.current.scrollTo({ left: targetLeft, behavior: "smooth" });
    }
  };

  if (!items || items.length === 0) return null;

  return (
    <CarouselWrapper>
      {/* Previous Arrow */}
      {canScrollLeft && imagesCount > 1 && (
        <ArrowButton style={{ left: 12 }} onClick={handleScrollPrev} aria-label="Previous">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </ArrowButton>
      )}

      {/* Main Horizontal Scroll View */}
      <ScrollContainer ref={containerRef} $gap={gap}>
        {items.map((item, idx) => (
          <div
            key={idx}
            ref={(el) => (itemRefs.current[idx] = el)}
            style={{ flexShrink: 0 }}
          >
            {item}
          </div>
        ))}
      </ScrollContainer>

      {/* Next Arrow */}
      {canScrollRight && imagesCount > 1 && (
        <ArrowButton style={{ right: 12 }} onClick={handleScrollNext} aria-label="Next">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </ArrowButton>
      )}

      {/* Dynamic Dots Indicator */}
      {(canScrollLeft || canScrollRight) && items.length > 1 && (
        <DotsContainer>
          {items.map((_, idx) => (
            <Dot
              key={idx}
              $active={activeIndex === idx}
              onClick={() => handleDotClick(idx)}
              aria-label={`Go to slide ${idx + 1}`}
            />
          ))}
        </DotsContainer>
      )}
    </CarouselWrapper>
  );
};

StreamCarousel.propTypes = {
  items: PropTypes.array.isRequired,
  scrollStep: PropTypes.number,
  gap: PropTypes.number,
};

export default StreamCarousel;
